import {
  Box,
  FlexAnchor,
  FlexAnchorProps,
  HelpLinkIFrame,
  Icon,
  ModalHeader,
  PrimaryButton,
  PrimaryButtonProps,
  smBumps,
  TertiaryButton,
  TertiaryButtonProps,
  Typo,
  useModal,
  xxsBumps,
} from '@wrisk/ui-components'
import React, { FC, MouseEvent, useCallback } from 'react'
import { Trans } from 'react-i18next'

import { PriceOption, Quote, RiskProfileStatus } from '../../../domain'
import {
  tCovers,
  TKey,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import {
  getOptionalCoverLink,
  getOptionalCoversContentBaseUrl,
  useConfig,
} from '../../../state/configuration'
import { useProduct } from '../../product/productContext'

export interface OptionalFlexAnchorProps
  extends Omit<FlexAnchorProps, 'onChange' | 'ref'> {
  cover: PriceOption
  loading?: boolean
  quote: Quote
  onChange?: (cover: PriceOption) => void
}

const tKey = TKey('components.optional-flex-anchor')

export const OptionalFlexAnchor: FC<OptionalFlexAnchorProps> = ({
  cover,
  loading,
  quote,
  onChange,
  ...props
}) => {
  const inQuote = cover.ratingStatus === RiskProfileStatus.QUOTE
  const showActionButton = inQuote && onChange
  const icon = inQuote && cover.active ? 'tick' : 'cross'
  const Button: FC<PrimaryButtonProps | TertiaryButtonProps> =
    inQuote && cover.active ? TertiaryButton : PrimaryButton
  const actionTKey = cover.active ? 'remove' : 'add'

  const { t } = useWriskTranslation()
  const modal = useModal()
  const { scheme } = useProduct()
  const contentBaseUrl = useConfig(getOptionalCoversContentBaseUrl(scheme.schemeCode))
  const link = getOptionalCoverLink(contentBaseUrl, quote.wordingVersion, cover)

  const onClick = useCallback(() => {
    switch (cover.ratingStatus) {
      case RiskProfileStatus.QUOTE:
        return modal.show({
          content: <HelpLinkIFrame src={link} />,
          variant: 'iframe',
        })
      case RiskProfileStatus.INSUFFICIENT_ACCURACY:
      case RiskProfileStatus.REFERRAL:
        return modal.show({
          content: (
            <Box>
              <ModalHeader
                header={t(tCovers(cover.coverCode, 'unavailable', 'header'))}
              />
              <Typo>
                <Trans
                  t={t}
                  i18nKey={tCovers(cover.coverCode, 'unavailable', 'description')}
                />
              </Typo>
            </Box>
          ),
        })
    }
  }, [cover, link, modal, t])

  const handleActionClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      onChange?.(cover)
    },
    [cover, onChange],
  )

  return (
    <FlexAnchor
      columnGap={smBumps}
      justifyContent='flex-start'
      onClick={onClick}
      px={smBumps}
      py={3}
      variant='highlight'
      {...props}
    >
      <Icon icon={icon} />
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        flexGrow={showActionButton ? 0 : 1}
        gap={xxsBumps}
      >
        <Typo>{t(tCovers(cover.coverCode, 'name'))}</Typo>
        <Icon icon='info' size='small' />
      </Box>
      {showActionButton && (
        <Button ml='auto' layout='small' loading={loading} onClick={handleActionClick}>
          {t(tKey('actions', actionTKey))}
        </Button>
      )}
    </FlexAnchor>
  )
}
